import { FC, useState } from 'react'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'

import { isRussia } from 'env'
import { formatBool } from 'helpers/formatBool'
import { refetchFunc } from 'modules/domain/common/hooks'
import { Company } from 'modules/domain/company/types'
import { CompanySize, User } from 'modules/domain/user/types'
import { Card } from 'views/components/Card/Card'
import { KeyValue } from 'views/components/KeyValue/KeyValue'
import { EditCompanyControl } from '../EditCompanyControl'
import { CompanyUsers } from './CompanyUsers'
import { Hr } from 'views/components/Hr/Hr'
import { DASH } from 'modules/constants'
import formatPrice from 'modules/utils/numbers/formatPrice'

type Props = {
  company: Company
  companyUsers?: User[]
  refreshCompany: refetchFunc
}

export const VatComponent: FC<{ company: Company }> = ({ company }) => {
  const { t } = useTranslation('company')
  return <KeyValue label={t('form.fields.vat')}>{formatBool(company.vat, t('common:thereIs'))}</KeyValue>
}

const TurnoverComponent: FC<{ company: Company }> = ({ company }) => {
  const { t } = useTranslation('company')
  if (!isRussia || !company.affectable_turnover_days) return null
  return <KeyValue label={t('turnover')}>{company.affectable_turnover_days}</KeyValue>
}

const CompanySizeComponent: FC<{ company: Company }> = ({ company }) => {
  const { t } = useTranslation('company')
  if (!isRussia) return null
  const sizeMap = { [CompanySize.big_company]: t('large'), [CompanySize.small_company]: t('small') }

  return (
    <KeyValue label={t('typeCompany')}>
      {company?.company_size ? sizeMap[company.company_size] : t('notSpecified')}
    </KeyValue>
  )
}

const formatSum = (value: number | null) => {
  return !_.isNil(value) ? formatPrice(value, true, false) : DASH
}

export const CompanyGeneral: FC<Props> = ({ company, companyUsers, refreshCompany }) => {
  const { t } = useTranslation('company')
  const [companyEditModalOpen, setCompanyEditModalOpen] = useState(false)

  const groupName = company.parent?.companies_group_name || company.companies_group_name

  return (
    <>
      <Card.Container>
        <Card.GapableContent>
          <Card.Title>{t('commonInfo')}</Card.Title>
          <Card.Container bordered>
            <Card.GapableContent>
              <Card.Grid cols={3} columnGap={16} maxColumnWidth="auto">
                <VatComponent company={company} />
                <KeyValue label={t('form.fields.prepayment')}>
                  {t(`form.prepaymentValues.${company.prepayment}`)}
                </KeyValue>
                <TurnoverComponent company={company} />
                <CompanySizeComponent company={company} />
                <KeyValue label={t('form.fields.payment_delay_days')}>{company.payment_delay_days}</KeyValue>
                <div style={{ gridColumn: 'span 3' }}>
                  <Hr />
                </div>
                <KeyValue label={t('creditLimit')}>{formatSum(company.credit_limit)}</KeyValue>
                <KeyValue label={t('accountsReceivable')}>{formatSum(company.debt)}</KeyValue>
                <KeyValue label={t('signedQuantity')}>{formatSum(company.remaining_signed_margin)}</KeyValue>
                <KeyValue label={t('availableCredit')}>{formatSum(company.available_credit_limit)}</KeyValue>
                {company.credit_limit_comment && (
                  <KeyValue label={t('creditLimitComment')} style={{ gridColumn: 'span 2' }}>
                    {company.credit_limit_comment}
                  </KeyValue>
                )}
              </Card.Grid>
              {groupName && (
                <div>
                  <KeyValue label={t('companies_group_name')}>{groupName}</KeyValue>
                </div>
              )}
            </Card.GapableContent>
            <Card.EditButton onClick={() => setCompanyEditModalOpen(true)} />
          </Card.Container>
        </Card.GapableContent>
        {!!companyUsers?.length && (
          <>
            <CompanyUsers users={companyUsers} />
          </>
        )}
      </Card.Container>

      {companyEditModalOpen && (
        <EditCompanyControl
          setCompanyEditModalOpen={setCompanyEditModalOpen}
          refreshCompany={refreshCompany}
          company={company}
        />
      )}
    </>
  )
}
